// Adapter para filtrar, paginar y ordenar datos de la tabla
export type DataTableAdapterOptions = {
	data: any[];
	search?: string;
	sorting?: { accessorKey: string; direction: 'asc' | 'desc' }[];
	pageIndex?: number;
	pageSize?: number;
};

export type LibranzaCredito = {
	tipoDeIdentificacionTitular: string;
	numeroDeIdentificacionTitular: string;
	nombreTitularCredito: string;
	numeroDeCredito: string;
	saldoTotalCredito: string;
	valorCuotaMensual: string;
	fechaVencimientoCuota: string;
	estadoDelPrestamo: string;
};

export function filterData(data: any[], search: string = "") {
	if (!data) return [];
	if (!search.trim()) return data;
	const lower = search.toLowerCase();
	return data.filter(row =>
		Object.values(row).some(
			v => (typeof v === 'string' || typeof v === 'number') &&
				v.toString().toLowerCase().includes(lower)
		)
	);
}

export function sortData(data: any[], sorting: { accessorKey: string; direction: 'asc' | 'desc' }[] = []) {
	if (!data) return [];
	if (!sorting.length) return data;
	return [...data].sort((a, b) => {
		for (const sort of sorting) {
			const aVal = a[sort.accessorKey];
			const bVal = b[sort.accessorKey];
			if (aVal === bVal) continue;
			if (sort.direction === 'asc') return aVal > bVal ? 1 : -1;
			else return aVal < bVal ? 1 : -1;
		}
		return 0;
	});
}

export function paginateData(data: any[], pageIndex: number = 0, pageSize: number = 5) {
	if (!data) return [];
	const start = pageIndex * pageSize;
	return data.slice(start, start + pageSize);
}

export function adaptLibranzasCreditosToRows(creditos: LibranzaCredito[]): Record<string, any>[] {
	return creditos.map((c) => ({
		id: c.numeroDeCredito,
		tipo: c.tipoDeIdentificacionTitular,
		documentoNumero: c.numeroDeIdentificacionTitular,
		nombre: c.nombreTitularCredito,
		fechaLimitePago: c.fechaVencimientoCuota,
		saldo: c.saldoTotalCredito,
		cuota: c.valorCuotaMensual,
		valorAPagar: c.valorCuotaMensual,
		estado: c.estadoDelPrestamo,
	}));
}

export type FilaCargada = {
	// UI field names
	id?: string;
	tipo?: string;
	documentoNumero?: string;
	valorAPagar?: string;
	valorTotal?: string;
	// API camelCase field names
	numeroDeCredito?: string;
	tipoDeIdentificacionTitular?: string;
	numeroDeIdentificacionTitular?: string;
	valorCuotaMensual?: string;
	// API field names as returned by the real endpoint (with spaces/capitals)
	'Numero de credito'?: string;
	'Tipo de documento'?: string;
	'Documento'?: string;
	'Valor a pagar'?: string;
};

const getFilaId = (f: FilaCargada) =>
	f.id ?? f.numeroDeCredito ?? f['Numero de credito'];
const getFilaValor = (f: FilaCargada, fallback: string) =>
	f['Valor a pagar'] ?? f.valorTotal ?? f.valorAPagar ?? f.valorCuotaMensual ?? fallback;
const getFilaTipo = (f: FilaCargada) =>
	f.tipo ?? f.tipoDeIdentificacionTitular ?? f['Tipo de documento'] ?? '';
const getFilaDocNum = (f: FilaCargada) =>
	f.documentoNumero ?? f.numeroDeIdentificacionTitular ?? f['Documento'] ?? '';

export function mergeCargarExcelFilasIntoRows(
	existing: Record<string, any>[],
	filas: FilaCargada[]
): Record<string, any>[] {
	if (filas.length === 0) return existing;

	const existingMap = new Map(
		existing.map((row) => [row.id as string, row])
	);

	return filas.flatMap((fila) => {
		const key = getFilaId(fila);
		if (!key) return [];
		const existingRow = existingMap.get(key);
		if (existingRow) {
			return [{ ...existingRow, valorAPagar: getFilaValor(fila, existingRow.valorAPagar) }];
		}
		return [{
			id: key,
			tipo: getFilaTipo(fila),
			documentoNumero: getFilaDocNum(fila),
			nombre: '',
			fechaLimitePago: '',
			saldo: '',
			cuota: '',
			valorAPagar: getFilaValor(fila, ''),
			estado: '',
		}];
	});
}

export function dataTableAdapter(options: DataTableAdapterOptions) {
	let data = options.data;
	data = filterData(data, options.search);
	data = sortData(data, options.sorting);
	const paginated = paginateData(data, options.pageIndex, options.pageSize);
	return {
		filtered: data,
		paginated,
		total: data.length,
		pageCount: Math.ceil(data.length / (options.pageSize || 5)),
	};
}
